import React from "react";
import { Link } from "react-router-dom";
import { API } from "../../../../common/Constants";
import { getAPIHeaders, getTenant } from "../../../../common/Utils";
import { all_routes } from "../../../router/all_routes";
import PreLoader from "../../../../core/common/preLoader";
import useFetch from "../../../../hooks/useFetch";
import CustomToast from "../../../../core/common/customToast";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import CopyRightInfo from "../../../../core/common/copyRightInfo";
import CommonTabMenu from "../commonTabMenu";
import SettingsMenu from "./setting-menu"
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";

interface CloudStorageConfig {
    accessKey: string;
    secretKey: string;
    bucketName: string;
    endpointUrl: string;
    region: string;
}

const CloudStorageSettings = () => {
    const routes = all_routes;
    const { isLoading, callFetch } = useFetch();
    const [toastSettings, setToastSettings] = React.useState<{
        show: boolean;
        message: string;
        heading: string;
        type: "ERROR" | "SUCCESS" | "INFO" | "";
    }>({
        show: false,
        message: "",
        heading: "",
        type: "ERROR",
    });

    const [cloudStorageConfig, setCloudStorageConfig] = React.useState<CloudStorageConfig>({
        accessKey: "",
        secretKey: "",
        bucketName: "",
        endpointUrl: "",
        region: ""
    });

    const [showSecretKey, setShowSecretKey] = React.useState<boolean>(false);

    React.useEffect(() => {
        // You can add a fetch function here to get existing config if needed
        // fetchCloudStorageConfig();
    }, []);

    const onToastClose = () => {
        setToastSettings({
            show: false,
            message: "",
            heading: "",
            type: "",
        });
    };

    const handleInputChange = (field: keyof CloudStorageConfig, value: string) => {
        setCloudStorageConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = (): boolean => {
        if (!cloudStorageConfig.accessKey.trim()) {
            setToastSettings({
                show: true,
                message: "Access Key is required.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        if (!cloudStorageConfig.secretKey.trim()) {
            setToastSettings({
                show: true,
                message: "Secret Key is required.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        if (!cloudStorageConfig.bucketName.trim()) {
            setToastSettings({
                show: true,
                message: "Bucket Name is required.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        if (!cloudStorageConfig.endpointUrl.trim()) {
            setToastSettings({
                show: true,
                message: "Endpoint URL is required.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        // Basic URL validation
        try {
            new URL(cloudStorageConfig.endpointUrl);
        } catch {
            setToastSettings({
                show: true,
                message: "Please enter a valid Endpoint URL.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        if (!cloudStorageConfig.region.trim()) {
            setToastSettings({
                show: true,
                message: "Region is required.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        return true;
    };

    const saveCloudStorageConfig = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        callFetch(API.SETTINGS_CLOUD_STORAGE_CONFIGS.replace(':tenantId', getTenant() || ''),
            'PUT',
            getAPIHeaders(),
            null,
            cloudStorageConfig
        ).then((res) => {
            setToastSettings({
                show: true,
                message: "Cloud storage configuration saved successfully.",
                heading: "Success",
                type: "SUCCESS",
            });
        }).catch((err) => {
            setToastSettings({
                show: true,
                message: "Failed to save cloud storage configuration.",
                heading: "Error",
                type: "ERROR",
            });
        });
    };

    const resetForm = () => {
        setCloudStorageConfig({
            accessKey: "",
            secretKey: "",
            bucketName: "",
            endpointUrl: "",
            region: "us-east-1"
        });
    };

    return (
        <div>
            <>
                <CustomToast
                    show={toastSettings?.show}
                    message={toastSettings?.message}
                    heading={toastSettings?.heading}
                    type={toastSettings?.type || "INFO"}
                    onClose={onToastClose} />
                <PreLoader showLoader={isLoading} />
                {/* Page Wrapper */}
                <div className="page-wrapper">
                    <div className="content">
                        {/* Breadcrumb */}
                        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                            <div className="my-auto mb-2">
                                <h2 className="mb-1">Application Settings</h2>
                                <nav>
                                    <ol className="breadcrumb mb-0">
                                        <li className="breadcrumb-item">
                                            <Link to={routes.adminDashboard}>
                                                <i className="ti ti-smart-home" />
                                            </Link>
                                        </li>
                                        <li className="breadcrumb-item">Administration</li>
                                        <li className="breadcrumb-item active" aria-current="page">
                                            Settings
                                        </li>
                                    </ol>
                                </nav>
                            </div>
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                        {/* /Breadcrumb */}
                        <CommonTabMenu activeTab="WebsiteSettings" />
                        <div className="row">
                            <div className="col-xl-3 theiaStickySidebar">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex flex-column list-group settings-list">
                                            <SettingsMenu activeTab="CloudStorageSettings" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-9">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="border-bottom mb-3 pb-3">
                                            <div className="d-flex align-items-center">
                                                <span className="avatar avatar-xl p-2 me-2 bg-light flex-shrink-0">
                                                    <ImageWithBasePath src="assets/img/icons/cloud.svg" alt="Cloud Storage" />
                                                </span>
                                                <div>
                                                    <h4>Cloud Storage Settings</h4>
                                                    <p className="text-muted mb-0">Configure Cloudflare R2 storage settings for your data</p>
                                                </div>
                                            </div>
                                        </div>
                                        <form onSubmit={saveCloudStorageConfig}>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">Access Key <span className="text-danger">*</span></label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter Access Key"
                                                            value={cloudStorageConfig.accessKey}
                                                            onChange={(e) => handleInputChange('accessKey', e.target.value)}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">Secret Key <span className="text-danger">*</span></label>
                                                        <div className="input-group">
                                                            <input
                                                                type={showSecretKey ? "text" : "password"}
                                                                className="form-control"
                                                                placeholder="Enter Secret Key"
                                                                value={cloudStorageConfig.secretKey}
                                                                onChange={(e) => handleInputChange('secretKey', e.target.value)}
                                                                required
                                                            />
                                                            <button
                                                                className="btn btn-outline-secondary"
                                                                type="button"
                                                                onClick={() => setShowSecretKey(!showSecretKey)}
                                                            >
                                                                <i className={`ti ti-${showSecretKey ? 'eye-off' : 'eye'}`} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">Bucket Name <span className="text-danger">*</span></label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter Bucket Name"
                                                            value={cloudStorageConfig.bucketName}
                                                            onChange={(e) => handleInputChange('bucketName', e.target.value)}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">Region <span className="text-danger">*</span></label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter Region"
                                                            value={cloudStorageConfig.region}
                                                            onChange={(e) => handleInputChange('region', e.target.value)}
                                                            required
                                                        />
                                                        <small className="form-text text-muted">Default: us-east-1</small>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <div className="mb-3">
                                                        <label className="form-label">Endpoint URL <span className="text-danger">*</span></label>
                                                        <input
                                                            type="url"
                                                            className="form-control"
                                                            placeholder="https://your-account-id.r2.cloudflarestorage.com"
                                                            value={cloudStorageConfig.endpointUrl}
                                                            onChange={(e) => handleInputChange('endpointUrl', e.target.value)}
                                                            required
                                                        />
                                                        <small className="form-text text-muted">Your Cloudflare R2 endpoint URL</small>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-light border me-3"
                                                    onClick={resetForm}
                                                >
                                                    Reset
                                                </button>
                                                <button type="submit" className="btn btn-primary">
                                                    Save Configuration
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
                        <CopyRightInfo />
                    </div>
                </div>
                {/* /Page Wrapper */}
            </>
        </div>
    );
};

export default CloudStorageSettings;
